import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Scale, 
  ArrowLeft,
  Shield,
  Eye,
  Lock,
  Database,
  Users,
  FileText,
  Cookie,
  AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-red-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                EduLegalHub
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                <ArrowLeft className="h-4 w-4 mr-2 inline" />
                Back to Home
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-4 py-2 mb-6">
              Privacy Protection
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                Privacy Policy
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              EduLegalHub values your privacy. This Privacy Policy explains how we collect, use, 
              store, and protect your personal information when you access or use EduLegalHub.
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <FileText className="h-4 w-4 mr-1" />
                Last Updated: 01/10/2025
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="pb-20 px-4">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto space-y-8">

            {/* Introduction */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-800">
                  <Shield className="h-6 w-6 mr-3" />
                  Our Commitment to Your Privacy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  EduLegalHub ("Platform", "we", "our", or "us") values your privacy. This Privacy Policy explains 
                  how we collect, use, store, and protect your personal information when you access or use EduLegalHub. 
                  By using the Platform, you consent to the practices described here.
                </p>
              </CardContent>
            </Card>

            {/* Section 1 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Users className="h-5 w-5 mr-2" />
                  1. Scope
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  This Privacy Policy applies to all users of EduLegalHub, including:
                </p>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Legal Document Users</strong> – individuals using our AI-assisted legal document services.</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Teachers & Educators</strong> – individuals subscribing to access lesson notes and teaching resources.</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Database className="h-5 w-5 mr-2" />
                  2. Information We Collect
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  We may collect the following types of information:
                </p>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">2.1 Personal Information</h4>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Name, email, phone number, and account details.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Payment details (processed securely via third-party providers).</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">School/teaching details (for teacher users).</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Case or document-related information entered into our forms.</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">2.2 Non-Personal Information</h4>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Browser type, IP address, device information.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Usage data (how you interact with the Platform).</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Cookies and similar technologies for analytics and preferences.</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 3 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Eye className="h-5 w-5 mr-2" />
                  3. How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use your information for the following purposes:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To provide and improve our services.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To generate draft legal documents based on your inputs.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To provide lesson notes and teaching materials.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To personalize your learning or document drafting experience.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To process payments and manage subscriptions.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To send updates, notifications, and support messages.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">To comply with Ghanaian laws and regulations.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Section 4 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">4. Legal Basis for Processing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed mb-4">
                  We process personal data under the following legal bases (as per Ghana's Data Protection Act, 2012):
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Consent</strong> – when you voluntarily provide information.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Contract</strong> – to fulfill subscription or service agreements.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Legal Obligation</strong> – where required by law or regulation.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Legitimate Interest</strong> – to improve services, ensure security, and prevent fraud.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Section 5 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Users className="h-5 w-5 mr-2" />
                  5. Sharing of Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-green-800 font-semibold">
                    🛡️ We do not sell or rent your data.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  However, we may share information with:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Service Providers</strong> (e.g., payment processors, cloud storage, analytics).</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Legal Authorities</strong> when required by law, court order, or regulation.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700"><strong>Partners/Consultants</strong> strictly for improving EduLegalHub's services.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Section 6 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">6. Data Retention</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Legal document data may be stored for reasonable periods for your access and platform improvement, unless you request deletion.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Teacher lesson note subscriptions and usage data are retained for as long as your account is active.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">We retain information only as long as reasonably necessary to fulfill service or legal obligations.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Section 7 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Lock className="h-5 w-5 mr-2" />
                  7. Security of Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Data is encrypted during transmission (SSL/HTTPS).</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Sensitive data (like payments) is handled by secure third-party providers.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Access controls and regular security reviews are in place.</span>
                  </li>
                </ul>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800 text-sm">
                    <AlertTriangle className="h-4 w-4 inline mr-1" />
                    Despite our efforts, no online system can be 100% secure — users share information at their own risk.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Section 8 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Shield className="h-5 w-5 mr-2" />
                  8. Your Rights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Under Ghana's Data Protection Act, 2012, you have the right to:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Access the personal information we hold about you.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Request correction of inaccurate or incomplete data.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Request deletion of your data ("Right to be Forgotten").</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Object to processing of your data.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">Withdraw consent at any time (where processing is based on consent).</span>
                  </li>
                </ul>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-sm">
                    Requests can be made through our contact details available on our Contact page.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Remaining Sections 9-12 */}
            <div className="grid md:grid-cols-1 gap-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-800">
                    <Cookie className="h-5 w-5 mr-2" />
                    9. Cookies and Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><span className="w-2 h-2 bg-purple-500 rounded-full inline-block mr-2"></span>EduLegalHub uses cookies to remember preferences, improve user experience, and analyze traffic.</p>
                  <p><span className="w-2 h-2 bg-purple-500 rounded-full inline-block mr-2"></span>You can disable cookies in your browser, but this may affect functionality.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">10. Third-Party Links</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">EduLegalHub may contain links to external sites (e.g., educational resources, court websites). We are not responsible for the privacy practices of third-party websites.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">11. Children's Privacy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">EduLegalHub does not knowingly collect information from children under 13. Teacher accounts for minors must be created with school or parental supervision.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">12. Changes to Privacy Policy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">We may update this Privacy Policy periodically. Users will be notified of material changes via email or platform notification. Continued use after changes constitutes acceptance.</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Section */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-600 to-pink-600 text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-semibold mb-4">Questions About Your Privacy?</h3>
                <p className="mb-6">If you have any questions about this Privacy Policy or how we handle your data, please contact us.</p>
                <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-full font-semibold hover:bg-gray-100 transition-colors">
                  Contact Us
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;